'use client'

import * as React from 'react'
import { z } from 'zod'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ErrorDisplay } from '@/components/ui/error-display'
import { useToast } from '@/components/ui/use-toast'
import { useUpdateProduct, useProductDetails, type UpdateProductInput } from '@/lib/api/products'
import { useUpdateVariant, type UpdateVariantInput } from '@/lib/api/variants'
import { ProductInformationForm, ProductSettingsForm, ProductVariantsForm } from '@/components/form/product'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'

// Edit schema: product fields + variants (with ids preserved for reconcile,
// prices kept as-is so their ids survive validation).
const editVariantSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1, 'Variant name is required'),
    description: z.string().nullish(),
    sku: z.string().nullable(),
    imageUrl: z.string().nullish(),
    grams: z.number().nullable(),
    active: z.boolean(),
    default: z.boolean(),
    price: z.number().nullable().optional(),
    compareAtPrice: z.number().nullable().optional(),
    prices: z.array(z.any()),
})

const editProductSchema = z.object({
    name: z.string().min(1, 'Product name is required'),
    description: z.string().nullish(),
    unitLabel: z.string().nullish(),
    accountingCode: z.string().nullish(),
    taxCategory: z.string().nullish(),
    mappedTaxCategoryId: z.string().nullish(),
    active: z.boolean(),
    isShippable: z.boolean(),
    isTaxable: z.boolean(),
    autoSync: z.boolean(),
    variants: z.array(editVariantSchema).min(1),
})
type EditProductFormData = z.infer<typeof editProductSchema>

const EMPTY_FORM: EditProductFormData = {
    name: '',
    description: null,
    unitLabel: null,
    accountingCode: null,
    taxCategory: null,
    mappedTaxCategoryId: null,
    active: true,
    isShippable: true,
    isTaxable: true,
    autoSync: true,
    variants: [],
}

const parseProductToFormData = (product: any): EditProductFormData => ({
    name: product.name || '',
    description: product.description ?? null,
    unitLabel: product.unitLabel ?? null,
    accountingCode: product.accountingCode ?? null,
    taxCategory: product.taxCategory ?? null,
    mappedTaxCategoryId: product.mappedTaxCategory?.id ?? null,
    active: product.active ?? true,
    isShippable: product.isShippable ?? true,
    isTaxable: product.isTaxable ?? true,
    autoSync: product.autoSync ?? true,
    variants: (product.variants || []).map((v: any) => ({
        id: v.id,
        name: v.name || '',
        description: v.description ?? null,
        sku: v.sku ?? null,
        imageUrl: v.imageUrl ?? null,
        grams: v.grams ?? null,
        active: v.active ?? true,
        default: v.default ?? false,
        price: null,
        compareAtPrice: null,
        prices: (v.prices || []).map((p: any) => ({ ...p })),
    })),
})

interface ProductEditDialogProps {
    storeId: string
    productId: string | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onSaved?: () => void
}

/**
 * Inline edit of the membership trigger product, including its variants and
 * price values. The variant/price *structure* is fixed (no add/remove of
 * variants or prices) — only values are editable.
 */
export const ProductEditDialog = ({ storeId, productId, open, onOpenChange, onSaved }: ProductEditDialogProps) => {
    const { t } = useTranslation(['products', 'common'])
    const { toast } = useToast()

    const { data, isLoading } = useProductDetails(productId ? [productId] : [], !!productId && open)
    const product = data?.[0]
    const updateProductMutation = useUpdateProduct()
    const updateVariantMutation = useUpdateVariant()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const methods = useForm<EditProductFormData>({
        defaultValues: EMPTY_FORM,
        resolver: zodResolver(editProductSchema),
        mode: 'onBlur',
    })
    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods

    React.useEffect(() => {
        if (open && product) reset(parseProductToFormData(product))
    }, [open, product, reset])

    const submit = async (formData: EditProductFormData) => {
        if (!productId) return
        try {
            setFormError(null)

            const productInput: UpdateProductInput = {
                productId,
                updatedData: {
                    name: formData.name,
                    description: formData.description,
                    unitLabel: formData.unitLabel,
                    accountingCode: formData.accountingCode,
                    taxCategory: formData.taxCategory,
                    mappedTaxCategoryId: formData.mappedTaxCategoryId,
                    active: formData.active,
                    isShippable: formData.isShippable,
                    isTaxable: formData.isTaxable,
                    autoSync: formData.autoSync,
                },
            }

            await Promise.all([
                updateProductMutation.mutateAsync(productInput),
                ...formData.variants
                    .filter((v) => v.id)
                    .map((v) =>
                        updateVariantMutation.mutateAsync({
                            variantId: v.id as string,
                            updatedData: {
                                name: v.name,
                                description: v.description ?? undefined,
                                sku: v.sku,
                                imageUrl: v.imageUrl ?? undefined,
                                grams: v.grams,
                                active: v.active,
                                default: v.default,
                                price: v.price ?? null,
                                compareAtPrice: v.compareAtPrice ?? null,
                                prices: v.prices,
                            },
                        } as UpdateVariantInput),
                    ),
            ])

            toast({
                title: t('productUpdated'),
                description: t('productUpdatedSuccess'),
            })
            onOpenChange(false)
            onSaved?.()
        } catch (error) {
            const parsed = parseTRPCError(error)
            setFormError(parsed)
            if (!parsed.isValidationError) {
                toast({
                    title: t('updateError'),
                    description: parsed.message,
                    variant: 'destructive',
                })
            }
        }
    }

    const showLoading = !!productId && isLoading && !product
    const isBusy = isSubmitting || updateProductMutation.isPending || updateVariantMutation.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-full sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{t('editProduct')}</DialogTitle>
                    <DialogDescription>
                        {t('editProductDescription')}
                    </DialogDescription>
                </DialogHeader>

                {showLoading ? (
                    <div className="flex h-48 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <FormProvider {...methods}>
                        <div className="max-h-[65vh] space-y-6 overflow-y-auto pr-1">
                            {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}
                            <ProductInformationForm />
                            <ProductVariantsForm
                                storeId={storeId}
                                hideAddPrice
                                hideRemovePrice
                                hideAddVariant
                                hideRemoveVariant
                            />
                            <ProductSettingsForm />
                        </div>
                    </FormProvider>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleSubmit(submit)} disabled={isBusy || showLoading}>
                        {isBusy ? t('updating') : t('saveChanges')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
